import { alpha, darken, getLuminance, lighten } from '@mui/material/styles';

/**
 * v2 design token values — cool-slate surface (default).
 *
 * Values are verbatim from docs/architecture/design-system.md and
 * the v2 prototype (V2_ACCENTS, V2_BARS_*,
 * V2_STATUS_*). Only the delivery mechanism changes — MUI palette instead
 * of CSS custom properties.
 *
 * Warm-sand surface is NOT built in Feature 01. The structure supports it;
 * add a `warmSand` sibling to `coolSlate` when needed.
 */

// ─── Accent registry ─────────────────────────────────────────────────────────

export type AccentName =
  | 'cobalt'
  | 'indigo'
  | 'violet'
  | 'forest'
  | 'sage'
  | 'graphite';

export type AccentTokens = {
  accent: string;
  hover: string;
  soft: string;
  tint: string;
  onAccent: string;
};

export type AccentEntry = {
  light: AccentTokens;
  dark: AccentTokens;
};

/** All six accents typed; only Indigo is populated in Feature 01. */
export const ACCENTS: Record<AccentName, AccentEntry> = {
  cobalt: {
    light: { accent: '#2E55E6', hover: '#1F44CC', soft: '#DCE5FB', tint: '#EEF2FE', onAccent: '#FFFFFF' },
    dark:  { accent: '#7395FF', hover: '#8AA8FF', soft: '#1D2A55', tint: '#161C30', onAccent: '#0A0D14' },
  },
  indigo: {
    light: { accent: '#5B5BD6', hover: '#4A4ABF', soft: '#E1E1F7', tint: '#EFEFFB', onAccent: '#FFFFFF' },
    dark:  { accent: '#9999EA', hover: '#ADADEF', soft: '#2B2A55', tint: '#1F1E3A', onAccent: '#0A0D14' },
  },
  violet: {
    light: { accent: '#7C4DD1', hover: '#683FB5', soft: '#E8DDF6', tint: '#F3ECFA', onAccent: '#FFFFFF' },
    dark:  { accent: '#B594E0', hover: '#C5A9EA', soft: '#3A2A56', tint: '#231B36', onAccent: '#0A0D14' },
  },
  forest: {
    light: { accent: '#2A7155', hover: '#1F5B43', soft: '#D2E5DC', tint: '#E7F0EB', onAccent: '#FFFFFF' },
    dark:  { accent: '#5FB088', hover: '#78C39E', soft: '#1B3C2D', tint: '#13281F', onAccent: '#0A0D14' },
  },
  sage: {
    light: { accent: '#5A8F73', hover: '#46765C', soft: '#DCE9E1', tint: '#EAF1ED', onAccent: '#FFFFFF' },
    dark:  { accent: '#8FC1A4', hover: '#A4CFB6', soft: '#2A4537', tint: '#1B2C24', onAccent: '#0A0D14' },
  },
  graphite: {
    light: { accent: '#1F2937', hover: '#0F1722', soft: '#DDE2EA', tint: '#EDEFF3', onAccent: '#FFFFFF' },
    dark:  { accent: '#E8ECF3', hover: '#FFFFFF', soft: '#2A3245', tint: '#181D29', onAccent: '#0A0D14' },
  },
};

// ─── Cool-slate surface / ink / line scales ───────────────────────────────────

export const coolSlate = {
  light: {
    bg:           '#F4F6FA',
    sidebar:      '#ECEFF4',
    surface:      '#FFFFFF',
    surface2:     '#F1F3F8',
    surface2Faint:'rgba(232,236,243,0.55)',
    surfaceSunk:  '#E8ECF3',
    ink:          '#0E141F',
    ink2:         '#414A59',
    ink3:         '#727B8B',
    ink4:         '#A8B0BD',
    line:         '#E1E5EC',
    line2:        '#CFD5DE',
    lineStrong:   '#0E141F',
  },
  dark: {
    bg:           '#0A0D14',
    sidebar:      '#0E121C',
    surface:      '#141925',
    surface2:     '#1A2030',
    surface2Faint:'rgba(20,25,37,0.6)',
    surfaceSunk:  '#070910',
    ink:          '#E8ECF3',
    ink2:         '#A4ACBA',
    ink3:         '#6C7585',
    ink4:         '#48505E',
    line:         '#222836',
    line2:        '#2E3547',
    lineStrong:   '#E8ECF3',
  },
} as const;

// ─── Status soft tints ────────────────────────────────────────────────────────
// Keep existing MUI semantic mains (success/warning/error/info); these are
// the NEW soft tints only, added under palette.statusSoft.

export const statusSoft = {
  light: { ok: '#D9EDE4', warn: '#EFE5CC', danger: '#F4D9D2', info: '#DEE6F9' },
  dark:  { ok: '#163829', warn: '#3F2F0E', danger: '#3E1F16', info: '#1D2A55' },
} as const;

// ─── Status saturated tones (legible foreground on the soft tints) ─────────────
// The v2 --ok/--warn/--danger/--info values. Deliberately MUTED vs the MUI
// semantic mains (success/warning/error/info.main), which stay vibrant for
// Alerts/Buttons. Use these for pill/badge text + the icon-on-tint pattern.

export const statusInk = {
  light: { ok: '#0F8F6F', warn: '#A67400', danger: '#C9462E', info: '#2E55E6' },
  dark:  { ok: '#4FBE9C', warn: '#E6B95C', danger: '#E27866', info: '#7395FF' },
} as const;

// ─── Project bar palette ──────────────────────────────────────────────────────
// Eight low-chroma pastels for timeline bars, project chips, and avatars.
// Avatar hashing: N = (hash(name) % 8) + 1.

export type BarIndex = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
export type BarEntry = { fill: string; ink: string };

export const bars: {
  light: Record<1 | 2 | 3 | 4 | 5 | 6 | 7 | 8, BarEntry>;
  dark:  Record<1 | 2 | 3 | 4 | 5 | 6 | 7 | 8, BarEntry>;
} = {
  light: {
    1: { fill: '#C7D6F5', ink: '#27396B' },
    2: { fill: '#C5E4DC', ink: '#23574A' },
    3: { fill: '#E6D2EE', ink: '#5A3A6B' },
    4: { fill: '#F4DCC7', ink: '#7A4A22' },
    5: { fill: '#D6E3CF', ink: '#43562F' },
    6: { fill: '#F1D6D3', ink: '#7A3934' },
    7: { fill: '#C7E2EE', ink: '#235870' },
    8: { fill: '#E3DDD0', ink: '#5C5239' },
  },
  dark: {
    1: { fill: '#3A4A78', ink: '#C7D6F5' },
    2: { fill: '#2E5045', ink: '#C5E4DC' },
    3: { fill: '#5A3F69', ink: '#E6D2EE' },
    4: { fill: '#6A4A28', ink: '#F4DCC7' },
    5: { fill: '#4F5E3E', ink: '#D6E3CF' },
    6: { fill: '#6F3A38', ink: '#F1D6D3' },
    7: { fill: '#2D5667', ink: '#C7E2EE' },
    8: { fill: '#544E3D', ink: '#E3DDD0' },
  },
};

// ─── Radius ───────────────────────────────────────────────────────────────────

export const radius = {
  default: 8,   // buttons, inputs, small chips — matches shape.borderRadius
  lg:      12,  // cards, panels
  bar:     4,   // chart bars, project chips
  pill:    999, // fully-rounded pills, status chips, progress-bar ends
} as const;

// ─── Shadows ──────────────────────────────────────────────────────────────────
// MUI's shadows[] array is fixed-length, so these live here and are applied
// via `sx` prop rather than the shadows[] index.

export const shadow = {
  light: {
    card:  '0 1px 0 rgba(15,23,42,0.02), 0 1px 2px rgba(15,23,42,0.04)',
    pop:   '0 12px 32px -12px rgba(15,23,42,0.18), 0 0 0 1px rgba(15,23,42,0.04)',
    inset: 'inset 0 1px 0 rgba(255,255,255,0.6)',
    bar:   '0 1px 0 rgba(15,23,42,0.04)',
  },
  dark: {
    card:  '0 1px 0 rgba(0,0,0,0.4), 0 2px 4px -2px rgba(0,0,0,0.5)',
    pop:   '0 12px 32px -12px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06)',
    inset: 'inset 0 1px 0 rgba(255,255,255,0.04)',
    bar:   '0 1px 0 rgba(255,255,255,0.04)',
  },
} as const;

// ─── Icon sizes ───────────────────────────────────────────────────────────────
// An explanatory icon's size is a decision about the type it sits beside, not an
// arbitrary pixel. Two sizes cover every site; both clear the 0.7rem floor for
// the text they accompany.

/** Inline with 11–13px type — a tile label, a table row, a badge. */
export const ICON_SIZE_INLINE = 13;

/** Beside 13–14px type — a panel header. */
export const ICON_SIZE_HEADER = 15;

// ─── Focus ring ───────────────────────────────────────────────────────────────
// Apply to all interactive elements. Colour should be the active accent.

export const FOCUS_RING = (colour: string): string =>
  `0 0 0 2px ${colour}`;

// ─── Proposed (pencilled-in) hatch ────────────────────────────────────────────
// A proposed allocation is a *texture*, not a hue: the bar keeps its project
// colour so it stays identifiable, and gains a 45° hatch in the FILL. No new
// colour enters the palette.
//
// The fill is the only place available. The planning board's allocation bar
// already spends dashed twice — a 1px dashed border on a non-project bar and a
// 2px dashed outline on a drag preview — and the Gantt spends `clipPath` on the
// half-day chamfer. A third edge cue would be unreadable, and a proposed
// non-project bar mid-drag would carry all three at once.

/** Drawn stripe width, px. */
export const HATCH_LINE_PX = 3;
/** Clear gap between stripes, px; gap + line is the repeat period. */
export const HATCH_GAP_PX = 4;
/** Stripes composite over the bar's own colour rather than replacing it. */
const HATCH_ALPHA = 0.7;
/** How far the stripe moves from the bar colour — enough to read, not a second hue. */
const HATCH_SHIFT = 0.32;
/**
 * Luminance below which a bar is treated as dark, so the stripe lightens rather
 * than darkens.
 *
 * Direction follows the **bar's own lightness**, not the theme mode, because
 * the two surfaces carrying this treatment use inverted colours: the planning
 * board's allocation bar is `bars[N].ink` (dark navy in light mode, pastel in
 * dark), while a timeline bar is `bars[N].fill` (pastel in light, dark in
 * dark). A rule keyed on the mode makes the hatch vanish on exactly half of
 * them, and it cannot cope with a project's own custom colour at all.
 */
const HATCH_DARK_BELOW = 0.3;

/**
 * `backgroundImage` marking a bar as proposed, for a bar of any colour.
 *
 * The stripes are the bar's own colour shifted toward whichever end has
 * headroom, at low alpha — never a flat grey, which would read as "disabled"
 * rather than "not yet agreed". Pair it with the bar's existing
 * `backgroundColor`; it sets no border and no clip path.
 */
export const proposedHatchImage = (barColour: string): string => {
  const stripe =
    getLuminance(barColour) < HATCH_DARK_BELOW
      ? lighten(barColour, HATCH_SHIFT)
      : darken(barColour, HATCH_SHIFT);
  const line = alpha(stripe, HATCH_ALPHA);
  return (
    `repeating-linear-gradient(45deg, transparent 0, transparent ${HATCH_GAP_PX}px, ` +
    `${line} ${HATCH_GAP_PX}px, ${line} ${HATCH_GAP_PX + HATCH_LINE_PX}px)`
  );
};

/**
 * Width of the fade drawn over the edge of a horizontally scrolling region that
 * still has content on that side. Wide enough to read as "there is more here",
 * narrow enough not to obscure the column it covers.
 */
export const FADE_WIDTH = 24;

/**
 * The edge fade itself. Built from the surface it sits on rather than a literal,
 * so it reads correctly in both themes — and faded to a *transparent copy of the
 * same colour*, never `transparent`, which some engines interpolate through
 * transparent-black and render as a grey smear.
 *
 * Cross-cutting rather than timesheets-local, because "this region scrolls and
 * you cannot see that it does" is a property of any table wider than its
 * container — the month summary found it first, the bench people grid second.
 * One definition, so the two cannot drift the way the unscheduled band and its
 * chart once did.
 */
export const scrollEdgeFade = (paperColour: string, side: 'left' | 'right'): string =>
  `linear-gradient(to ${side === 'left' ? 'right' : 'left'}, ${paperColour}, ${alpha(paperColour, 0)})`;

/** Drawn line width for a *cell* hatch, px — a hairline, not a stripe. */
const CELL_HATCH_LINE_PX = 1;
/** Clear gap between cell hatch lines, px. Line + gap is the repeat period. */
const CELL_HATCH_GAP_PX = 6;
/** Cell hatch lines sit well back — they are a ground, never a foreground mark. */
const CELL_HATCH_ALPHA = 0.45;

/**
 * `backgroundImage` marking a **week cell** (or a slice of one) as time that is
 * not ordinary available capacity — leave, or before a new starter's hire date.
 *
 * Deliberately a different texture from {@link proposedHatchImage}, which marks
 * *bars*: these two vocabularies share the planning board's screen and must not
 * be mistaken for each other. This one is finer and further back — 1px every
 * 7px at 45% alpha — because a cell already carries its own hue and, when it is
 * wide enough, an icon; the texture only has to say "not normal", not carry the
 * whole meaning. Keep both surfaces on this helper so the legend's swatch and
 * the grid never drift apart.
 */
export const cellHatchImage = (lineColour: string): string => {
  const line = alpha(lineColour, CELL_HATCH_ALPHA);
  return (
    `repeating-linear-gradient(45deg, transparent 0, transparent ${CELL_HATCH_GAP_PX}px, ` +
    `${line} ${CELL_HATCH_GAP_PX}px, ${line} ${CELL_HATCH_GAP_PX + CELL_HATCH_LINE_PX}px)`
  );
};
